// Shared presentational helpers for the customer drawer sections.
// Extracted from the former single-file CustomerDrawer (task 0067.02) — logic unchanged.

import { useId } from "react";
import { Lock } from "lucide-react";
import { Input } from "@/shared/ui/Input";
import { DatePicker } from "@/shared/ui/DatePicker";

export function SectionTitle({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h4
      className={`mb-3.5 border-b border-grey-100 pb-2 text-[13px] font-bold uppercase tracking-[0.04em] text-grey-900 ${className}`}
    >
      {children}
    </h4>
  );
}

export interface FieldProps {
  label: string;
  value: string | undefined;
  changed: boolean;
  onChange: (v: string) => void;
  placeholder?: string;
  // Optional input type — defaults to "text". Pass "date" for date pickers.
  type?: string;
  // false = read-only view (task 0153): the drawer opens locked until the operator presses Edit.
  editing?: boolean;
  // Read-only view only: keep [start, end] characters and hide the middle (passport, PINFL).
  mask?: [number, number];
}

/** Hide the middle of a sensitive value: maskMiddle("AB1234567", 2, 2) → "AB•••••67". */
function maskMiddle(value: string, keepStart: number, keepEnd: number): string {
  const v = value.trim();
  if (v.length <= keepStart + keepEnd) return "•".repeat(v.length);
  return v.slice(0, keepStart) + "•".repeat(v.length - keepStart - keepEnd) + v.slice(v.length - keepEnd);
}

// "YYYY-MM-DD" → "DD.MM.YYYY" for the read-only view; anything else is shown as-is.
function displayValue(value: string, type: string, mask?: [number, number]): string {
  if (!value) return "";
  if (mask) return maskMiddle(value, mask[0], mask[1]);
  if (type === "date" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value.split("-").reverse().join(".");
  return value;
}

export function Field({
  label,
  value,
  changed,
  onChange,
  placeholder,
  type = "text",
  editing = true,
  mask,
}: FieldProps) {
  // Associate the label with the input so screen readers announce the field name (WCAG 1.3.1).
  const id = useId();
  if (!editing) {
    // Locked view: a read-only input (still selectable for copy) — never the raw masked value.
    const shown = displayValue(value ?? "", type, mask);
    return (
      <div className="mb-4 flex flex-col gap-[7px]">
        <label htmlFor={id} className="text-[13px] font-semibold text-grey-500">{label}</label>
        <div className="relative">
          <Input
            id={id}
            readOnly
            aria-readonly="true"
            value={shown || "—"}
            className={`tnum cursor-default border-grey-100 bg-grey-25 focus-visible:border-grey-200 focus-visible:ring-0 ${
              shown ? "text-grey-800" : "text-grey-400"
            } ${mask && shown ? "pr-9 tracking-[0.04em]" : ""}`}
          />
          {mask && shown && (
            <Lock
              size={14}
              strokeWidth={2}
              aria-hidden
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-grey-300"
            />
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="mb-4 flex flex-col gap-[7px]">
      <label htmlFor={id} className="text-[13px] font-semibold text-grey-700">{label}</label>
      {/* `changed` = unsaved edit — amber warning border, not a validation error (no `invalid`).
          The warning classes override the default grey-200 border at rest and on hover. */}
      {type === "date" ? (
        <DatePicker
          id={id}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={onChange}
          className={changed ? "border-warning bg-warning-bg hover:border-warning" : undefined}
        />
      ) : (
        <Input
          id={id}
          type={type}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={changed ? "border-warning bg-warning-bg hover:border-warning" : undefined}
        />
      )}
    </div>
  );
}

/** Format a balance string (DRF Decimal) as Uzbek so'm with thousands separator. */
export function formatBalance(raw: string): string {
  const n = Number(raw);
  if (isNaN(n)) return raw;
  return n.toLocaleString("uz-UZ") + " so'm";
}
